import React, { useState, useEffect, useMemo } from "react"
import { Button, Table, Tag, Flex, Card, Spin } from "antd"
import { DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { getAssignedExperiences, removeTripFromCrewMember, checkTripBookingConflicts } from "store/crew-profiles"
import { toaster } from "components/common/Toast"
import { ConfirmModal } from "ui/ConfirmModal"
import RemoveExperienceWithBookingsModal from "ui/RemoveExperienceWithBookingsModal"
import { useTranslation } from "react-i18next"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import NoAssignedExperience from "ui/Icon/DSIcon/NoAssignedExperience"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import { colors } from "themes/variables"
import { Text } from "ui/Typography"

const AssignedExperiences = ({ crewMemberId, onAssign, crewMemberName = "", can_edit }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [experienceToRemove, setExperienceToRemove] = useState(null)
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [showBookingsModal, setShowBookingsModal] = useState(false)
  const [experienceToRemoveName, setExperienceToRemoveName] = useState("")
  const [experienceBookings, setExperienceBookings] = useState([])

  // Get assigned experiences and bookings from Redux
  const { data: assignedExperiencesData = [], loading } = useSelector((state) => state.crewProfiles.assignedExperiences)
  const { loading: removing } = useSelector((state) => state.crewProfiles.removeExperience)
  const { data: conflictsData, loading: checkingConflicts } = useSelector((state) => state.crewProfiles.tripBookingConflicts)

  // Fetch assigned experiences on mount
  useEffect(() => {
    if (crewMemberId) {
      dispatch(getAssignedExperiences(crewMemberId))
    }
  }, [dispatch, crewMemberId])

  // Handle conflict check results
  useEffect(() => {
    if (!checkingConflicts && conflictsData && experienceToRemove) {
      // Transform conflicting bookings to match the modal format
      const conflictingBookings = conflictsData.conflicting_bookings || []

      if (conflictingBookings.length > 0) {
        // Map bookings to a single flat array
        const formattedBookings = conflictingBookings.map((booking) => ({
          id: booking.booking_id,
          experienceName: booking.experience_name || experienceToRemoveName,
          time: `${booking.start_time} - ${booking.end_time}`,
          date: booking.booked_date || "",
          imageUrl: booking.preview || "",
          location: booking.address || "",
          booking_uuid: booking.booking_uuid
        }))

        setExperienceBookings(formattedBookings)
        setShowBookingsModal(true)
      } else {
        setShowRemoveConfirmModal(true)
      }
    }
  }, [checkingConflicts, conflictsData, experienceToRemove, experienceToRemoveName])

  // Transform API data to component format
  const experiences = useMemo(() => {
    return assignedExperiencesData.map((exp) => ({
      id: exp.trip_id,
      trip_assignment_id: exp.trip_assignment_id,
      title: exp.trip_title,
      name: exp.trip_title,
      type: exp.trip_type,
      role: exp.assignment_role,
      guest_count: exp.guest_count || "-",
      price: exp.price || 0,
      currency: "$",
      status: exp.status || "draft"
    }))
  }, [assignedExperiencesData])

  const handleAssign = () => {
    if (onAssign) {
      onAssign()
    }
  }

  /**
   * Show confirmation modal before removing an experience
   */
  const handleRemoveClick = (experienceIdToRemove) => {
    // Prevent removal if this is the only experience
    if (experiences.length === 1) {
      return
    }

    // Find the experience details
    const experience = experiences.find((exp) => exp.id === experienceIdToRemove)
    if (!experience) {
      return
    }

    // Store experience details for later use (store trip_assignment_id for removal)
    setExperienceToRemove(experience.trip_assignment_id)
    setExperienceToRemoveName(experience.title || experience.name)

    // Check if this experience has any booking conflicts using trip_id and profile_id
    dispatch(checkTripBookingConflicts(experienceIdToRemove, crewMemberId, "profile"))
  }

  /**
   * Actually remove the experience after confirmation
   */
  const handleConfirmRemove = async () => {
    if (experienceToRemove && crewMemberId) {
      try {
        const response = await dispatch(removeTripFromCrewMember(crewMemberId, experienceToRemove))
        toaster.success(
          response?.message || t("equipment.details.experience_removed_success", { defaultValue: "Experience removed successfully." })
        )
        // Refresh the assigned experiences list after successful removal
        dispatch(getAssignedExperiences(crewMemberId))
      } catch (error) {
        console.error("Error removing experience:", error)
      }
    }
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
  }

  /**
   * Handle cancellation of experience removal
   */
  const handleCancelRemove = () => {
    setShowRemoveConfirmModal(false)
    setShowBookingsModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
    setExperienceBookings([])
  }

  // Show loading state
  if (loading && !assignedExperiencesData.length) {
    return (
      <Flex vertical gap={16} className={styles.container}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>
            {t("equipment.details.assigned_experiences", { defaultValue: "Assigned Experiences" })}{" "}
            <span className={styles.count}>(0)</span>
          </h2>
        </Flex>
        <Card className="py-5xl">
          <Flex justify="center" align="center">
            <Spin size="large" />
          </Flex>
        </Card>
      </Flex>
    )
  }

  // Table columns for assigned experiences
  const experienceColumns = [
    {
      title: t("equipment.details.columns.experience_name", { defaultValue: "Experience Name" }),
      dataIndex: "title",
      key: "title",
      render: (_, record) => record.title || record.name
    },
    {
      title: t("equipment.details.columns.guests", { defaultValue: "Guests" }),
      dataIndex: "max_guests",
      key: "max_guests",
      render: (_, record) => record.guest_count || "-"
    },
    {
      title: t("equipment.details.columns.price", { defaultValue: "Price" }),
      key: "price",
      render: (_, record) => {
        const price = Number.parseFloat(record.price || 0)
        const currency = record.currency || "$"
        return `${currency}${price.toFixed(2)}`
      }
    },
    {
      title: t("equipment.details.columns.status", { defaultValue: "Status" }),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const isPublished = status?.toLowerCase() === "published"
        const displayStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "Draft"
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null} className={styles.statusTag}>
            {displayStatus}
          </Tag>
        )
      }
    },
    can_edit && {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => {
        const isOnlyExperience = experiences.length === 1
        const isRemoving = checkingConflicts && experienceToRemove === record.trip_assignment_id
        return (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className={styles.deleteButton}
            onClick={() => handleRemoveClick(record.id)}
            disabled={isOnlyExperience || checkingConflicts}
            loading={isRemoving}
            title={
              isOnlyExperience
                ? t("equipment.details.cannot_remove_last_experience", { defaultValue: "Cannot remove the last experience" })
                : ""
            }
          />
        )
      }
    }
  ].filter(Boolean)

  return (
    <Flex vertical gap={16} className={styles.container}>
      {/* Header */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <h2 className={styles.title}>
          {t("equipment.details.assigned_experiences", { defaultValue: "Assigned Experiences" })}{" "}
          <span className={styles.count}>({experiences.length})</span>
        </h2>
        {can_edit && (
          <Button type="link" onClick={handleAssign} className={styles.assignButton}>
            {t("equipment.details.assign", { defaultValue: "Assign" })}
          </Button>
        )}
      </Flex>

      {/* Table with Empty State */}
      <WithEmptyStateWrapper
        name="assigned experience"
        hasItems={experiences.length > 0}
        fallback={
          <Card className="py-5xl">
            <Flex vertical justify="center" align="center" gap={12}>
              <NoAssignedExperience />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.experiences.emptyState.title")}
              </Text>
              <Text color={colors.textSecondaryDark}>{t("crewMember.experiences.emptyState.subtitle")}</Text>
            </Flex>
          </Card>
        }
      >
        <div className={styles.scrollContainer}>
          <Table dataSource={experiences} columns={experienceColumns} pagination={false} rowKey="id" className={styles.experiencesTable} />
        </div>
      </WithEmptyStateWrapper>

      {/* Remove Experience Confirmation Modal (No Bookings) */}
      <ConfirmModal
        open={showRemoveConfirmModal}
        title={t("equipment.remove_experience", { defaultValue: "Remove Experience?" })}
        info={t("equipment.confirm_remove_experience_text", {
          defaultValue: `Are you sure you want to remove <strong>${experienceToRemoveName}</strong> from this crew member?`,
          experienceName: experienceToRemoveName
        })}
        onCancel={handleCancelRemove}
        onDelete={handleConfirmRemove}
        deleteLabel={t("common.remove", { defaultValue: "Remove" })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        hideCancelButton={false}
        loading={removing}
      />

      {/* Remove Experience Modal (With Bookings) */}
      <RemoveExperienceWithBookingsModal
        open={showBookingsModal}
        onCancel={handleCancelRemove}
        resourceName={crewMemberName}
        experienceName={experienceToRemoveName}
        bookings={experienceBookings}
        resourceType="crew"
      />
    </Flex>
  )
}

AssignedExperiences.propTypes = {
  crewMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onAssign: PropTypes.func,
  crewMemberName: PropTypes.string,
  can_edit: PropTypes.bool
}

export default AssignedExperiences
